/**
 * The SSE frame codec. Zero runtime dependencies, so it runs unchanged in the
 * browser client and in the headless conformance runner.
 *
 * The decoder is line oriented, which is what the reference parser does. A
 * frame is emitted the moment its `data:` line completes, not when the trailing
 * blank line arrives. The blank line does one thing only: it resets the current
 * event name back to `message`. Buffering happens at the line level, so a
 * partial line, and therefore a partial frame, survives a chunk boundary.
 *
 * Three ordering rules are load bearing and each has a test.
 *
 *   1. The discriminator is the `event:` line, never a field inside `data`.
 *   2. The `id:` line arrives before `data:` on the wire, but the cursor it
 *      carries belongs to the frame and must be applied after the payload. The
 *      decoder attaches the pending id to the frame it completes, and the state
 *      machine advances only once that frame has been applied.
 *   3. The `[DONE]` sentinel is skipped before dispatch and before cursor
 *      delivery, so it advances nothing. The pending id survives it, exactly as
 *      it does in the reference parser.
 */

/** A frame as it comes off the wire, before any interpretation. */
export type DecodedFrame =
  | { kind: 'event'; event: string; id: number | null; data: unknown }
  | { kind: 'sentinel' }
  | { kind: 'keepalive' }
  | { kind: 'malformed'; raw: string; reason: string };

/** The default event name. A blank line resets to this. */
const DEFAULT_EVENT_NAME = 'message';

/** A `data:` line with exactly this content is the sentinel. */
const DONE_SENTINEL = '[DONE]';

const EVENT_FIELD = 'event:';
const ID_FIELD = 'id:';
const DATA_FIELD = 'data:';

/**
 * Serialise one frame. Field order matches the wire: event, then id, then data,
 * then the blank line that terminates the frame.
 *
 * The payload is guaranteed to be a single line because `JSON.stringify`
 * escapes every newline inside a string and never emits one between tokens
 * when no indentation is requested. That guarantee is what rule 3 of the wire
 * contract rests on, and `encodeFrame` never passes an indent. The event name
 * carries no such guarantee, so it is checked: a name containing a line break
 * would split one frame into two and hand the reader a payload with no event.
 */
export function encodeFrame(event: string, id: number | null, data: unknown): string {
  if (event.includes('\n') || event.includes('\r')) {
    throw new Error(
      `encodeFrame received an event name containing a line break: ${JSON.stringify(event)}. ` +
        'That would split one frame into two. See docs/PROTOCOL.md rule 3.',
    );
  }

  const payload = JSON.stringify(data ?? null);
  const idLine = id === null ? '' : `${ID_FIELD} ${String(id)}\n`;
  return `${EVENT_FIELD} ${event}\n${idLine}${DATA_FIELD} ${payload}\n\n`;
}

/**
 * The sentinel line. Emitted before the terminal `done` frame to match real
 * behaviour. It carries no id, and a conforming client advances nothing on it.
 */
export function encodeSentinel(): string {
  return `${DATA_FIELD} ${DONE_SENTINEL}\n\n`;
}

/**
 * A keepalive comment. It carries no id and must not advance the cursor. It
 * does reset the byte watchdog, which is the point of sending it, and it does
 * not reset the content watchdog. That difference is what separates a stalled
 * stream from a disconnected one.
 */
export function encodeKeepalive(): string {
  return ': keepalive\n\n';
}

export interface FrameDecoder {
  /** Feed one chunk of bytes. Returns every frame that completed within it. */
  push(chunk: Uint8Array): DecodedFrame[];
  /** Close the stream. Returns any final frames, plus a truncated line if one is left over. */
  flush(): DecodedFrame[];
}

/**
 * A streaming decoder.
 *
 * The `TextDecoder` is held across calls and used with `{ stream: true }`, so a
 * multi byte UTF-8 sequence split across a chunk boundary decodes correctly
 * rather than turning into a replacement character. The `unicode_split`
 * scenario proves this. It is a real concern in this protocol rather than a
 * theoretical one: the reference parser decodes the same way.
 */
export function createFrameDecoder(): FrameDecoder {
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = DEFAULT_EVENT_NAME;
  let pendingId: number | null = null;
  let closed = false;

  const handleLine = (rawLine: string, out: DecodedFrame[]): void => {
    // A trailing carriage return means the producer sent CRLF. A lone carriage
    // return is not treated as a line terminator, which matches the reference.
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

    if (line === '') {
      currentEvent = DEFAULT_EVENT_NAME;
      return;
    }

    if (line.startsWith(':')) {
      out.push({ kind: 'keepalive' });
      return;
    }

    if (line.startsWith(EVENT_FIELD)) {
      currentEvent = line.slice(EVENT_FIELD.length).trim();
      return;
    }

    if (line.startsWith(ID_FIELD)) {
      const raw = line.slice(ID_FIELD.length).trim();
      // The cursor is opaque on the wire and the reference passes it through as
      // a string. Our server emits monotonic integers. A cursor that does not
      // read as an integer is one we cannot use, which is not the same thing as
      // a malformed frame, so it is dropped rather than reported.
      pendingId = /^\d+$/.test(raw) ? Number.parseInt(raw, 10) : null;
      return;
    }

    if (!line.startsWith(DATA_FIELD)) {
      // An unknown field. The format reserves the right to add them and a
      // reader is required to ignore what it does not recognise.
      return;
    }

    const payload = line.slice(DATA_FIELD.length).trim();

    if (payload === DONE_SENTINEL) {
      // Skipped before dispatch and before cursor delivery. The pending id is
      // deliberately left in place: the reference reaches its cursor step only
      // on a line it dispatched, so a sentinel never advances anything.
      out.push({ kind: 'sentinel' });
      return;
    }

    let data: unknown;
    try {
      data = JSON.parse(payload);
    } catch {
      // Reported, never thrown, so the client can skip one bad frame and keep
      // going. The pending id is dropped with it: advancing the cursor past a
      // frame that was never applied is how a resume silently loses output.
      pendingId = null;
      out.push({ kind: 'malformed', raw: payload, reason: 'data line is not valid JSON' });
      return;
    }

    out.push({ kind: 'event', event: currentEvent, id: pendingId, data });
    pendingId = null;
  };

  /** Consume every complete line in the buffer. Returns whatever is left over. */
  const drain = (out: DecodedFrame[]): string => {
    const lastBreak = buffer.lastIndexOf('\n');
    if (lastBreak === -1) return buffer;

    for (const line of buffer.slice(0, lastBreak).split('\n')) handleLine(line, out);
    return buffer.slice(lastBreak + 1);
  };

  return {
    push(chunk: Uint8Array): DecodedFrame[] {
      if (closed) return [];

      const out: DecodedFrame[] = [];
      buffer += decoder.decode(chunk, { stream: true });
      buffer = drain(out);
      return out;
    },

    flush(): DecodedFrame[] {
      if (closed) return [];
      closed = true;

      const out: DecodedFrame[] = [];
      // Flushing surfaces any incomplete multi byte sequence left at the end of
      // the stream as a replacement character, rather than dropping the bytes.
      buffer += decoder.decode();
      const residue = drain(out);
      buffer = '';

      const trailing = residue.endsWith('\r') ? residue.slice(0, -1) : residue;
      if (trailing !== '') {
        out.push({ kind: 'malformed', raw: trailing, reason: 'stream ended mid line' });
      }

      return out;
    },
  };
}
